import { useState, useEffect } from 'react';
import { Users, Shield, UserPlus, User, Settings as SettingsIcon, UsersRound, Bell } from 'lucide-react';
import apiClient from '../api/client';
import CustomFieldsTab from '../components/settings/CustomFieldsTab';
import TeamsTab from '../components/settings/TeamsTab';
import MembersTab from '../components/settings/MembersTab';
import RolesTab from '../components/settings/RolesTab';
import NotificationPreferencesTab from '../components/settings/NotificationPreferencesTab';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [workspace, setWorkspace] = useState(null);
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Get current user from localStorage
      const userStr = localStorage.getItem('user');
      if (userStr) {
        setCurrentUser(JSON.parse(userStr));
      }

      // Load workspace info
      const workspaceRes = await apiClient.get('/api/v1/workspaces/current');
      if (workspaceRes.data.success) {
        setWorkspace(workspaceRes.data.data);
      }

      // Load members
      if (workspaceRes.data.data?.workspace?.id) {
        const membersRes = await apiClient.get(`/api/v1/workspaces/${workspaceRes.data.data.workspace.id}/members`);
        setMembers(membersRes.data.data || []);

        // Load roles
        const rolesRes = await apiClient.get(`/api/v1/workspaces/${workspaceRes.data.data.workspace.id}/roles`);
        setRoles(rolesRes.data.data || []);
      }
    } catch (error) {
      console.error('Error loading settings data:', error);
      alert('Failed to load settings data');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'profile', name: 'Profile', icon: User },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'members', name: 'Members', icon: Users },
    { id: 'teams', name: 'Teams', icon: UsersRound },
    { id: 'roles', name: 'Roles', icon: Shield },
    { id: 'custom-fields', name: 'Configurable Fields', icon: SettingsIcon },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl text-gray-600">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">Manage your profile and team</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-300 mb-6">
        <div className="flex gap-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'profile' && (
        <ProfileTab currentUser={currentUser} workspace={workspace} />
      )}

      {activeTab === 'notifications' && (
        <NotificationPreferencesTab />
      )}

      {activeTab === 'members' && (
        <MembersTab
          members={members}
          roles={roles}
          workspace={workspace}
          onRefresh={loadData}
          showInviteDialog={showInviteDialog}
          setShowInviteDialog={setShowInviteDialog}
        />
      )}

      {activeTab === 'teams' && (
        <TeamsTab />
      )}

      {activeTab === 'roles' && (
        <RolesTab 
          roles={roles} 
          workspace={workspace}
          onRefresh={loadData}
        />
      )}

      {activeTab === 'custom-fields' && (
        <CustomFieldsTab workspace={workspace?.workspace} />
      )}
    </div>
  );
};

// Profile Tab Component
const ProfileTab = ({ currentUser, workspace }) => {
  // Check if user is admin (can see Environment info)
  const isAdmin = workspace?.role?.role_name?.toLowerCase() === 'admin' || 
                  workspace?.role?.permissions?.members?.invite;

  return (
    <div className="bg-white rounded-lg shadow p-6 max-w-2xl">
      <h2 className="text-xl font-semibold mb-6">Profile Information</h2>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
          <div className="text-base text-gray-900">
            {currentUser?.first_name} {currentUser?.last_name}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <div className="text-base text-gray-900">{currentUser?.email}</div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
          <div className="text-base text-gray-900">{workspace?.role?.role_name || currentUser?.role}</div>
        </div>

        {/* Environment info - only visible to admins */}
        {isAdmin && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Environment</label>
            <div className="text-base text-gray-900">{workspace?.workspace?.name}</div>
          </div>
        )}

        {workspace?.role?.assigned_oems && workspace.role.assigned_oems.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assigned OEMs</label>
            <div className="flex flex-wrap gap-2">
              {workspace.role.assigned_oems.map((oem) => (
                <span key={oem} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                  {oem}
                </span>
              ))}
            </div>
          </div>
        )}

        {workspace?.role?.assigned_regions && workspace.role.assigned_regions.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Regions</label>
            <div className="flex flex-wrap gap-2">
              {workspace.role.assigned_regions.map((region) => (
                <span key={region} className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm">
                  {region}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
